import { Box, Stack, Tab, Tabs } from "@mui/material";
import { PrimaryPaper } from "Components/Shared/PrimaryPaper";
import { AffiliateLinkWithActions } from "Components/Affiliate/AffiliateLinkWithActions";
import { useSearchParams } from "Navigation/useSearchParams";
import { CustomerAffiliateDescription } from "Components/Affiliate/Customer/CustomerAffiliateDescription";
import { Resources, useResource } from "Translations/Resources";
import { LeadStatus } from "Infrastructure/Api/Api";
import { AccessRoleCodes } from "Models/AccessRoleCodes";
import { FilteredLeadList } from "Components/Affiliate/Stats/FilteredLeadList";
import { AffiliateEarningsSummary } from "Components/Affiliate/Earnings/AffiliateEarningsSummary";
import type { AffiliateTabItem } from "Components/Affiliate/types";

const RESOURCES = Resources.Components.Affiliate.MerchantAffiliate;

export const MerchantAffiliatePageContent = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useResource();

  const tabs: Array<AffiliateTabItem> = [
    {
      id: "customer",
      label: t(RESOURCES.Tabs.Customer),
      content: (
        <Stack gap={4}>
          <AffiliateEarningsSummary />
          <PrimaryPaper>
            <CustomerAffiliateDescription />
            <AffiliateLinkWithActions
              invitedUserRole={AccessRoleCodes.Customer}
            />
          </PrimaryPaper>
          <Stack gap={1}>
            <FilteredLeadList
              leadStatus={LeadStatus.Invited}
              invitedAsRole={AccessRoleCodes.Customer}
            />
            <FilteredLeadList
              leadStatus={LeadStatus.AnonymousPaymentPerformed}
            />
            <FilteredLeadList
              registeredAsRole={AccessRoleCodes.Customer}
              leadStatus={LeadStatus.Registered}
            />
          </Stack>
        </Stack>
      ),
    },
  ];

  const activeTab = searchParams.get("activeTab") || tabs[0].id;

  return (
    <Box display="flex" alignItems="center" flexDirection="column">
      <Tabs
        value={activeTab}
        onChange={(_e, id: string) => setSearchParams({ activeTab: id })}
        variant="scrollable"
        sx={{
          width: "100%",
          "& .MuiTabs-scrollButtons.Mui-disabled": {
            opacity: 0.3,
          },
        }}
      >
        {tabs.map(({ label, id }) => (
          <Tab label={label} value={id} key={id} />
        ))}
      </Tabs>
      <Box mt="0.5rem" width="100%" maxWidth="600px">
        <Stack gap={4}>
          {tabs.map(({ content, id }) => (
            <div role="tabpanel" hidden={id !== activeTab} key={id}>
              {id === activeTab && content}
            </div>
          ))}
        </Stack>
      </Box>
    </Box>
  );
};
